import { FC, useContext, useEffect, useMemo, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import * as dealActions from '@payaca/store/deals/dealsActions';

import ListedDealsTable from '@/ui/components/listedDealsTable/ListedDealsTable';
import PageBanner from '@/ui/components/pageBanner/PageBanner';
import AuthenticatedPageWrapper from '../pageWrappers/authenticatedPageWrapper/AuthenticatedPageWrapper';

import { ListedDealViews, SortBy } from '@payaca/types/listedDealTypes';
import { SortDirection } from '@payaca/types/listViewTypes';

import useGetMyAccountPipelines from '@/api/queries/me/useGetMyAccountPipelines';
import { useTranslation } from '@/i18n';
import ProjectsContextProvider, {
  ProjectContextType,
  ProjectsContext,
} from '@/ui/components/contextProviders/ProjectsContextProvider';
import ProjectFilters from '@/ui/components/projectFilters/ProjectFilters';
import Button from '@payaca/components/button/Button';
import {
  ButtonColourVariant,
  ButtonStyleVariant,
} from '@payaca/components/button/enums';
import { getUrlSearchParam } from '@payaca/helpers/urlHelper';
import { Helmet } from 'react-helmet';
import z from 'zod/v4';
import './ListedDealsPage.sass';

type Props = {
  showArchivedDeals?: boolean;
};

const ListedDealsPage: FC<Props> = ({
  showArchivedDeals,
}: Props): JSX.Element => {
  const history = useHistory();
  const translate = useTranslation();
  const location = useLocation();

  const initialRequestDataSearchParam = getUrlSearchParam(
    location.search,
    'initialRequestData'
  );

  const initialRequestData = useMemo(() => {
    if (!initialRequestDataSearchParam?.length) return;

    try {
      return JSON.parse(
        decodeURIComponent(initialRequestDataSearchParam)
      ) as ProjectContextType['requestData'];
    } catch (e) {
      return;
    }
  }, [initialRequestDataSearchParam]);

  const displayTypeSearchParam = getUrlSearchParam(
    location.search,
    'displayType'
  );

  const initialDisplayType = z
    .enum(ListedDealViews)
    .safeParse(displayTypeSearchParam).data;

  return (
    <ProjectsContextProvider
      archived={showArchivedDeals}
      initialRequestData={initialRequestData}
      initialDisplayType={initialDisplayType}
    >
      <Helmet title={translate('pages.deals.title')} />
      <AuthenticatedPageWrapper className="listed-deals-page">
        {showArchivedDeals && (
          <PageBanner>
            <div className="archived-deals-banner">
              <span>You are currently viewing your archived Projects</span>
              <Button
                onClick={() => history.push('/deals')}
                styleVariant={ButtonStyleVariant.ANCHOR}
                colourVariant={ButtonColourVariant.SECONDARY}
              >
                Go back
              </Button>
            </div>
          </PageBanner>
        )}
        <div className="listed-deals-table-container">
          <ProjectFilters />
          <Body />
        </div>
      </AuthenticatedPageWrapper>
    </ProjectsContextProvider>
  );
};

export default ListedDealsPage;

const Body: FC = () => {
  const history = useHistory();
  const { displayType, requestData } = useContext(ProjectsContext);
  const { data: pipelines } = useGetMyAccountPipelines();

  if (requestData.pipelineId && displayType === ListedDealViews.PIPELINE) {
    const pipeline = pipelines?.find(
      (p) => p.id === requestData.pipelineId?.toString()
    );
    if (pipeline) {
      history.replace(`/pipelines/${pipeline.publicId}`);
      return;
    }
  }

  return <ProjectsTable />;
};

const ProjectsTable: FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { requestData: baseRequestData } = useContext(ProjectsContext);

  const [requestData, setRequestData] = useState({
    ...baseRequestData,
    sortBy: SortBy.LAST_UPDATED,
    sortDirection: SortDirection.DESCENDING,
    pageNumber: 1,
    pageSize: 20,
  });

  useEffect(() => {
    setRequestData((rd) => ({ ...rd, ...baseRequestData, pageNumber: 1 }));
  }, [baseRequestData]);

  useEffect(() => {
    const { assignedUserIds, ...rd } = requestData;

    dispatch(
      dealActions.requestGetListedDealsPage({
        ...rd,
        userAssignments: assignedUserIds?.map((x) =>
          x === -1 ? 'unassigned' : x
        ),
      })
    );
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [requestData]);

  return (
    <ListedDealsTable
      sortBy={requestData.sortBy}
      sortDirection={requestData.sortDirection}
      onClickRow={(projectId) => history.push(`/projects/${projectId}`)} // TODO: should use publicId
      onSelectPage={(pageNumber) =>
        setRequestData((rd) => ({ ...rd, pageNumber }))
      }
      onChangeSortOrder={(sortBy, sortDirection) =>
        setRequestData((rd) => ({ ...rd, sortBy, sortDirection }))
      }
      onSelectPageSize={(pageSize) =>
        setRequestData((rd) => ({ ...rd, pageSize }))
      }
    />
  );
};
